import { useCallback, useEffect, useRef, useState } from 'react';
import {
  chargeAmountProblems,
  parseRmbAmount,
  withTopUpFee,
  type ChargeStep,
  type ChinaPaymentType,
  type PayToAccount,
  type PercentFeeRule,
  type Quote,
} from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card, Icon, cn } from '@mypoz/ui';

import { VERIFICATION_GATE_ENABLED, useAccountProfile } from '@/features/account/index.js';
import {
  CHARGE_STEPS,
  initialChargeFields,
  toChargeDraft,
  typeFields,
  type ChargeFields,
} from '@/features/china-payment/chargeFields.js';
import { AmountStep } from '@/features/china-payment/components/AmountStep.js';
import { ChargeSteps } from '@/features/china-payment/components/ChargeSteps.js';
import { DetailsStep } from '@/features/china-payment/components/DetailsStep.js';
import { PaymentStep } from '@/features/china-payment/components/PaymentStep.js';
import { RequestSent } from '@/features/china-payment/components/RequestSent.js';
import {
  CHINA_PAYMENT_TYPE_RULES,
  TOP_UP_MAX_WITHOUT_FEE_RULE_RMB_CENTS,
} from '@/features/china-payment/config.js';
import { fetchQuote } from '@/features/china-payment/api.js';
import { useQuote, type QuoteState } from '@/features/china-payment/useQuote.js';
import { useSendCharge } from '@/features/china-payment/useSendCharge.js';
import { useReceiptUploads } from '@/features/orders/useReceiptUploads.js';
import { useFormat } from '@/lib/format.js';
import { actionLinkClass } from '@/lib/styles.js';

/** `useReceiptUploads` keeps receipts per key; a request has exactly one. */
const RECEIPT_KEY = 'charge';

/** The sticky app header's height and a little air: `scroll-mt-20`, in pixels. */
const HEADER_CLEARANCE_PX = 80;

/** How long a quote is taken as it stands before the request is confirmed; then it is asked again. */
const QUOTE_FRESH_MS = 10 * 60 * 1000;

export interface NewRequestCardProps {
  /** The request types offered, in tab order. One type shows no tabs. */
  types: readonly ChinaPaymentType[];
  /** The selected type. The page owns it, so it can live in the address (`?type=`). */
  type: ChinaPaymentType;
  onTypeChange: (type: ChinaPaymentType) => void;
  /** The same form on the old website, for what the new app does not take. */
  oldSiteUrl: string;
  /** The accounts to pay into (MYP-61), for step 3. */
  accounts: readonly PayToAccount[];
  accountsStatus: 'loading' | 'ready' | 'error';
  onRetryAccounts: () => void;
  /** The rate card's top-up fee rule, for top-ups from ¥500 (`withTopUpFee`). */
  topUpFee?: PercentFeeRule;
}

/**
 * The "New request" card (MYP-60, MYP-109): a China Payment request in the old website's three
 * steps — the amount and its live quote, the customer's details, then the transfer and its receipt
 * — sent with `ChargeResult` for staff to process by hand. No payment gateway is involved: the
 * customer transfers RM to a MyPoz account themselves.
 *
 * Which types can be sent, and up to what amount, is `CHINA_PAYMENT_TYPE_RULES` (`config.ts`): a
 * type that is off shows a link to the same form on mypoz.com instead. An unverified account sees
 * the verification notice in place of Next, as the old website refuses it ("未认证者，是无法充值的").
 *
 * The tabs show on step 1 only: the details belong to one type. Switching type keeps the amount,
 * asks for a new quote, and clears the fields that belong to the old type. Going Back keeps
 * everything typed, and the receipt.
 */
export function NewRequestCard({
  types,
  type,
  onTypeChange,
  oldSiteUrl,
  accounts,
  accountsStatus,
  onRetryAccounts,
  topUpFee,
}: NewRequestCardProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { profile } = useAccountProfile();
  const rules = CHINA_PAYMENT_TYPE_RULES[type];
  const topUp = type === 'ALIPAYCHARGE' || type === 'WXCHARGE';
  // Without the rate card's fee rule a top-up from ¥500 cannot be priced (`withTopUpFee`).
  const maxRmbCents =
    rules.maxRmbCents ?? (topUp && !topUpFee ? TOP_UP_MAX_WITHOUT_FEE_RULE_RMB_CENTS : undefined);
  const limits = maxRmbCents === undefined ? {} : { maxRmbCents };
  const verified = !VERIFICATION_GATE_ENABLED || profile.verification === 'verified';

  const [amount, setAmount] = useState('');
  const [step, setStep] = useState<ChargeStep>('amount');
  // The quote Next was pressed on: steps 2 and 3, and the request, use exactly these figures.
  const [pinned, setPinned] = useState<Quote | undefined>(undefined);
  const pinnedAt = useRef(0);
  const [fields, setFields] = useState<ChargeFields>(() => initialChargeFields(type, profile));
  const [sent, setSent] = useState<{ reference?: string } | undefined>(undefined);
  const receipts = useReceiptUploads();
  const onSent = useCallback((reference?: string) => {
    setSent(reference ? { reference } : {});
  }, []);
  const sender = useSendCharge(onSent);
  const busy = sender.state === 'sending' || sender.state === 'checking';

  /*
    The type can change under the form — a tab on step 1, or the address (an old China Payment
    link). The fields that belong to one type are reset then, during render (React's "storing
    information from previous renders"), so no frame shows a Taobao account in the box now
    labelled "WeChat ID", or a China recipient on a top-up; and the request starts again at the
    amount. What every type shares stays: the customer's details, the receipt and the remark.
  */
  const [fieldsType, setFieldsType] = useState(type);
  if (fieldsType !== type) {
    setFieldsType(type);
    setFields((current) => ({ ...current, ...typeFields(type, profile) }));
    setStep('amount');
    setPinned(undefined);
  }

  const rmbCents = parseRmbAmount(amount);
  const overLimit =
    rmbCents !== undefined && limits.maxRmbCents !== undefined && rmbCents > limits.maxRmbCents;
  // No quote is asked for a type that is off, or above its limit: those quotes are known wrong.
  const apiQuote = useQuote(type, rules.submit && !overLimit ? amount : '');
  // The API's quote, with the old website's 3% on top-ups from ¥500 (`withTopUpFee`).
  const quote: QuoteState = (() => {
    if (!apiQuote.quote) return apiQuote;
    const priced = withTopUpFee(apiQuote.quote, topUpFee);
    return priced
      ? { ...apiQuote, quote: priced }
      : { status: 'error', stale: true, retry: apiQuote.retry };
  })();

  const slot = receipts.slots[RECEIPT_KEY]?.[0];
  const draft = toChargeDraft(
    type,
    fields,
    pinned
      ? { rmbCents: pinned.rmbCents, totalRmCents: pinned.totalRmCents }
      : rmbCents === undefined
        ? {}
        : { rmbCents },
    receipts.paths[RECEIPT_KEY]?.[0],
  );
  const change = (patch: Partial<ChargeFields>) =>
    setFields((current) => ({ ...current, ...patch }));

  // Each step starts at its top, announced: focus goes to the step's (screen-reader) heading.
  const stepHeading = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const heading = stepHeading.current;
    if (!heading) return;
    heading.focus({ preventScroll: true });
    // Back to the card's top when it has scrolled under the sticky header (its `scroll-mt-20`).
    const card = heading.closest('[data-surface="card"]');
    if (card && card.getBoundingClientRect().top < HEADER_CLEARANCE_PX) {
      card.scrollIntoView({ block: 'start' });
    }
  }, [step]);

  // Step 3 confirms on the quote it shows; one older than QUOTE_FRESH_MS is asked again first.
  const ensureFreshQuote = async (): Promise<'fresh' | 'changed' | 'failed'> => {
    if (!pinned) return 'failed';
    if (Date.now() - pinnedAt.current < QUOTE_FRESH_MS) return 'fresh';
    try {
      const next = withTopUpFee(await fetchQuote(type, pinned.rmbCents), topUpFee);
      if (!next) return 'failed';
      pinnedAt.current = Date.now();
      setPinned(next);
      if (chargeAmountProblems(next, limits).length > 0) {
        // Below the minimum now, say: the amount step says so.
        setStep('amount');
        return 'changed';
      }
      return next.totalRmCents === pinned.totalRmCents ? 'fresh' : 'changed';
    } catch (cause) {
      console.error('[china-payment] could not refresh the quote', cause);
      return 'failed';
    }
  };

  const startAgain = () => {
    sender.reset();
    if (slot) receipts.remove(RECEIPT_KEY, slot.id);
    setSent(undefined);
    setAmount('');
    setPinned(undefined);
    setFields(initialChargeFields(type, profile));
    setStep('amount');
  };

  const stepNumber = CHARGE_STEPS.indexOf(step) + 1;

  return (
    <Card className="scroll-mt-20">
      <h2 className="text-base font-semibold text-ink">{t('chinaPayment.newRequest.title')}</h2>

      {sent ? (
        <div className="mt-4">
          <RequestSent
            {...(sent.reference ? { reference: sent.reference } : {})}
            onNewRequest={startAgain}
          />
        </div>
      ) : (
        <>
          {step !== 'amount' ? null : types.length > 1 ? (
            <div
              role="group"
              aria-label={t('chinaPayment.request.typeLabel')}
              // Two by two on a phone, one row from `sm:` up — every tab visible, none scrolled away.
              className="mt-4 grid grid-cols-2 gap-0.5 rounded-md border border-line p-0.5 sm:inline-flex"
            >
              {types.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={option === type}
                  onClick={() => onTypeChange(option)}
                  className={cn(
                    'min-h-11 rounded px-4 text-sm font-medium transition-colors lg:min-h-9',
                    option === type ? 'bg-ink text-surface' : 'text-muted hover:text-ink',
                  )}
                >
                  {t(`chinaPayment.types.${option}`)}
                </button>
              ))}
            </div>
          ) : (
            <p className="mt-1 text-sm text-muted">{t(`chinaPayment.types.${type}`)}</p>
          )}

          {rules.submit ? (
            <>
              <div className="mt-4">
                <ChargeSteps current={step} />
              </div>
              <h3 ref={stepHeading} tabIndex={-1} className="sr-only">
                {`${t('chinaPayment.form.stepCount', { step: stepNumber, total: CHARGE_STEPS.length })}: ${t(`chinaPayment.form.steps.${step}`)}`}
              </h3>

              {step !== 'amount' && pinned ? (
                <div className="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-lg bg-surface-alt px-4 py-2 text-sm">
                  <p className="min-w-0">
                    <span className="font-medium text-ink">{t(`chinaPayment.types.${type}`)}</span>{' '}
                    <span className="text-muted tabular-nums">
                      {t('chinaPayment.form.summary', {
                        rmb: fmt.money(pinned.rmbCents, 'RMB'),
                        rm: fmt.money(pinned.totalRmCents, 'RM'),
                      })}
                    </span>
                  </p>
                  <button
                    type="button"
                    onClick={() => setStep('amount')}
                    // Not while a request is out: its answer belongs to the step that sent it.
                    disabled={busy}
                    className={actionLinkClass('text-sm disabled:opacity-60')}
                  >
                    {t('chinaPayment.form.change')}
                  </button>
                </div>
              ) : null}

              <div className="mt-4">
                {step === 'amount' || !pinned ? (
                  <AmountStep
                    amount={amount}
                    onAmountChange={setAmount}
                    quote={quote}
                    limits={limits}
                    verified={verified}
                    oldSiteUrl={oldSiteUrl}
                    onNext={(settled) => {
                      pinnedAt.current = Date.now();
                      setPinned(settled);
                      setStep('details');
                    }}
                  />
                ) : step === 'details' ? (
                  <DetailsStep
                    draft={draft}
                    fields={fields}
                    onChange={change}
                    onBack={() => setStep('amount')}
                    onNext={() => setStep('payment')}
                  />
                ) : (
                  <PaymentStep
                    draft={draft}
                    limits={limits}
                    quote={pinned}
                    fields={fields}
                    onChange={change}
                    accounts={accounts}
                    accountsStatus={accountsStatus}
                    onRetryAccounts={onRetryAccounts}
                    receipt={{
                      ...(slot ? { slot } : {}),
                      onPick: (file) => {
                        // One receipt per request: a new pick replaces the old one.
                        if (slot) receipts.remove(RECEIPT_KEY, slot.id);
                        receipts.attach(RECEIPT_KEY, file);
                      },
                      onRetry: () => slot && receipts.retry(RECEIPT_KEY, slot.id),
                      onRemove: () => slot && receipts.remove(RECEIPT_KEY, slot.id),
                    }}
                    onBack={() => setStep('details')}
                    onFix={setStep}
                    ensureFreshQuote={ensureFreshQuote}
                    sender={sender}
                  />
                )}
              </div>
            </>
          ) : (
            <div className="mt-4 flex items-start gap-3 rounded-lg border border-line bg-surface-alt p-4">
              <Icon name="info" size={20} className="shrink-0 text-muted" />
              <div className="text-sm text-ink">
                <p>{t('chinaPayment.newRequest.unavailable')}</p>
                <a
                  href={oldSiteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={actionLinkClass('mt-1 inline-flex text-sm')}
                >
                  {t('chinaPayment.newRequest.goToMypoz')}
                </a>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
